import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  ACC_DOCUMENT_CONTENT_TYPE_OPTIONS,
  ACC_DOCUMENT_DATE_FORMAT,
  AccDocumentContentType,
  IAccClaimFundingRequest,
  IAccDocumentMetadata,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef, toMoment } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';

type AccSendDocumentsDialogForm = Pick<
  IAccDocumentMetadata,
  | 'contentType'
  | 'claimReferenceNumber'
  | 'clientNHINumber'
  | 'clientFirstName'
  | 'clientLastName'
  | 'fileExtension'
> & { clientDOB: moment.Moment };

export interface IAccSendDocumentsDialogData {
  claimRequest: WithRef<IAccClaimFundingRequest>;
  patient: WithRef<IPatient>;
}

const PDF_FILE_EXTENSION = 'PDF';

@Component({
  selector: 'pr-acc-send-documents-dialog',
  imports: [NgDialogModule, InformationBoxComponent, MatDatepickerModule],
  templateUrl: './acc-send-documents-dialog.component.html',
  styleUrl: './acc-send-documents-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
})
export class AccSendDocumentsDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<AccSendDocumentsDialogComponent, IAccDocumentMetadata>>(
      MatDialogRef
    );

  data = inject<IAccSendDocumentsDialogData>(MAT_DIALOG_DATA);

  form = new TypedFormGroup<AccSendDocumentsDialogForm>({
    contentType: new TypedFormControl<AccDocumentContentType>(
      AccDocumentContentType.PhotoXrays,
      Validators.required
    ),
    claimReferenceNumber: new TypedFormControl<string>(
      this.data.claimRequest.data.claimData.claimNumber,
      Validators.required
    ),
    clientNHINumber: new TypedFormControl<string>(
      this.data.patient.nationalHealthIndexNumber ??
        this.data.claimRequest.data.claimData.patient?.details.nhi,
      Validators.required
    ),
    clientFirstName: new TypedFormControl<string>(
      this.data.claimRequest.data.claimData.patient?.details.firstName,
      Validators.required
    ),
    clientLastName: new TypedFormControl<string>(
      this.data.claimRequest.data.claimData.patient?.details.surname,
      Validators.required
    ),
    clientDOB: new TypedFormControl<moment.Moment>(
      toMoment(
        this.data.patient.dateOfBirth ??
          this.data.claimRequest.data.claimData.patient?.details.dateOfBirth ??
          moment()
      ),
      Validators.required
    ),
    fileExtension: new TypedFormControl<string>(
      PDF_FILE_EXTENSION,
      Validators.required
    ),
  });

  contentTypeOptions = ACC_DOCUMENT_CONTENT_TYPE_OPTIONS;

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formData = this.form.getRawValue();
    this._dialogRef.close({
      ...formData,
      clientDOB: formData.clientDOB.format(ACC_DOCUMENT_DATE_FORMAT),
    });
  }
}
